/** @jsxImportSource @emotion/react */
import { faAngleRight, faBarcodeRead } from "@fortawesome/pro-regular-svg-icons";
import dayjs from "dayjs";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Layer } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { toast } from "../../../../utils/toast";
import MetaForm from "../../../Forms/MetaForm";
import QRCodeScan from "../../../Forms/QRCodeScan";
import { Button } from "../../../Helper/Button";
import StartCheckFromOverviewModal from "../../Layers/StartCheckFromOverviewModal";
import useRipeningRoom, { useIsItemAllocated } from "../hooks/useRipeningRoom";

export function ScanToCheckModal({ isInBottomBar }: { isInBottomBar?: boolean }) {
    const { pallets, refreshAllocatedLayers, activeRipeningCycle } = useRipeningRoom();
    const config = useConfig();
    const { location_id } = useParams<{ location_id: string }>();

    const [pickerOpen, setPickerOpen] = useState(false);
    const [query, setQuery] = useState<{ layer_id?: string }>({});
    const [modalCheckType, setModalCheckType] = useState<string | null>(null);

    const isItemAllocatedOnDate = useIsItemAllocated();
    const today = dayjs();
    const activePallets = pallets.filter((p: any) => isItemAllocatedOnDate(p.id, today));

    const handleScan = (result: string) => {
        const layer = activePallets.find((p: any) => p.label === result);
        if (!layer) {
            toast.info("Pallet not found");
            return;
        }
        setQuery((prev) => ({ ...prev, layer_id: (layer as any).id }));
    };

    const findPalletForm = [
        {
            type: "element",
            el: <QRCodeScan onScan={handleScan} barcode_filter={config.barcode_filter} />
        },
        {
            type: "radio",
            label: "Pallet",
            name: "layer_id",
            options: activePallets.map((p: any) => ({ label: p.label, value: p.id }))
        },
    ];

    const pallet = activePallets.find((p: any) => p.id === query.layer_id) as Layer | undefined;

    // Determine eligible check types for the scanned pallet
    const eligibleLocations = pallet
        ? config.get_check_types_config(pallet).filter((l: any) => l.start_on_ripening_room)
        : [];

    const hasAnyEligibleLocation = activePallets.some((p: any) => config.get_check_types_config(p).some((l: any) => (
        l.start_on_ripening_room
    )));

    const handleCheckModalClose = () => setModalCheckType(null);

    const handleSuccess = () => {
        setModalCheckType(null);
        setPickerOpen(false);
        setQuery({});
        refreshAllocatedLayers();
    };

    const disabled = activePallets.length === 0 || !hasAnyEligibleLocation;

    return (
        <>
            {/* Pallet picker modal */}
            <Modal isOpen={pickerOpen} size="md" toggle={() => setPickerOpen(false)}>
                <ModalHeader toggle={() => setPickerOpen(false)}>Scan to check</ModalHeader>
                <ModalBody>
                    <MetaForm
                        meta={findPalletForm}
                        object={query}
                        config={config}
                        setValue={(field, value) => setQuery((prev) => ({ ...prev, [field]: value }))}
                    />
                    {pallet && eligibleLocations.length > 0 && (
                        <div className="d-flex flex-column gap-2 mt-3">
                            {eligibleLocations.map((loc: any) => (
                                <Button
                                    key={loc.value}
                                    color="primary"
                                    disabled={!!loc.requires_active_ripening_cycle && !activeRipeningCycle}
                                    icon={faAngleRight}
                                    onClick={() => setModalCheckType(loc.value)}
                                >
                                    {loc.text} check
                                </Button>
                            ))}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setPickerOpen(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Unified check modal */}
            {pallet && modalCheckType && (
                <StartCheckFromOverviewModal
                    isOpen={Boolean(modalCheckType)}
                    onClose={handleCheckModalClose}
                    layer={pallet}
                    checkLocationValue={modalCheckType}
                    groupItems={[pallet]}
                    selectionMode
                    onSuccess={handleSuccess}
                    onRefresh={refreshAllocatedLayers}
                    extraCheckPayload={location_id ? { location_id } : undefined}
                />
            )}

            <Button
                disabled={disabled}
                color="primary"
                onClick={() => setPickerOpen(true)}
                icon={faBarcodeRead}
                isInBottomBar={isInBottomBar}
            >
                Scan to check
            </Button>
        </>
    );
}

